import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Loader2, Send, CheckCircle2, AlertTriangle } from "lucide-react";

const DOT = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-600" };
const LABEL = { green: "OK", amber: "ATTENTION", red: "ACTION NEEDED" };
const TXT = { green: "text-emerald-600", amber: "text-amber-600", red: "text-red-600" };
const DIR = { up: "▲", down: "▼", flat: "▬" };

export default function DailyDigestPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/digest/preview"); setData(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/digest/send-now");
      toast.success(`Digest sent to ${data.recipients?.length || 0} admin(s)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="daily-digest-page" className="max-w-2xl">
      <div className="flex items-center gap-2 mb-1"><Mail className="w-6 h-6 text-[#B01E28]" /><h1 className="font-display text-2xl font-bold tracking-tight">Daily Digest</h1></div>
      <p className="text-sm text-slate-500 mb-5">Your once-a-day health &amp; flow snapshot (backups, AI spend, intake, attention, security). Emailed to admins each morning — here's the live view, plus AI spend. Dollars are admin-only.</p>

      <div className="flex items-center gap-3 mb-5">
        <Button data-testid="digest-refresh" variant="outline" onClick={load} disabled={loading} className="rounded-lg h-9">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}</Button>
        <Button data-testid="digest-send-now" onClick={sendNow} disabled={sending} className="rounded-lg h-9 bg-[#E0781A] hover:bg-[#C76714] text-white">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Send me one now</>}
        </Button>
      </div>

      {loading && !data ? (
        <div className="font-mono text-xs text-slate-400 py-10">BUILDING SNAPSHOT…</div>
      ) : data && (
        <>
          {data.healthy ? (
            <div data-testid="digest-banner-healthy" className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <div><div className="font-bold text-emerald-700">ALL HEALTHY</div><div className="text-xs text-emerald-700">Nothing needs you today.</div></div>
            </div>
          ) : (
            <div data-testid="digest-banner-issues" className="rounded-xl border border-red-300 bg-red-50 p-4 mb-4">
              <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-5 h-5 text-red-600" /><div className="font-bold text-red-700">{data.reds.length + data.ambers.length} item(s) need attention</div></div>
              <ul className="list-disc list-inside text-sm">
                {data.reds.map((r, i) => <li key={`r${i}`} className="text-red-700">{r}</li>)}
                {data.ambers.map((a, i) => <li key={`a${i}`} className="text-amber-700">{a}</li>)}
              </ul>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {(data.sections || []).map((s) => (
              <div key={s.key} data-testid={`digest-section-${s.key}`} className="flex items-start gap-3 p-4">
                <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${DOT[s.state]}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2"><span className="font-semibold text-slate-800 text-sm">{s.title}</span><span className={`text-[10px] font-bold ${TXT[s.state]}`}>{LABEL[s.state]}</span></div>
                  <div className="text-[13px] text-slate-600 mt-0.5">{s.msg}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Auto-sent daily at 12:00 UTC. Refine what's included any time.</p>

          {data.trend?.rows && (
            <div data-testid="digest-trend" className="rounded-xl border border-slate-200 bg-white p-4 mt-4">
              <div className="text-sm font-bold text-slate-800 mb-0.5">7-day trend</div>
              <div className="text-[11px] text-slate-400 mb-3">Slow drift — intake backing up or cost creeping — before it becomes a problem.</div>
              <div className="overflow-x-auto">
                <table className="w-full text-center border-collapse">
                  <thead>
                    <tr className="text-[10px] text-slate-400">
                      <th className="text-left font-normal py-1"></th>
                      {data.trend.rows.map((r) => <th key={r.date} className="font-normal px-1.5 py-1">{r.date.slice(5)}</th>)}
                      <th className="px-1.5 py-1 text-[#B01E28]">avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="text-[13px] text-slate-700">
                      <td className="text-left text-[11px] text-slate-500 whitespace-nowrap py-1.5">Parts entered {DIR[data.trend.entered_direction]}</td>
                      {data.trend.rows.map((r) => <td key={r.date} className="px-1.5 font-semibold">{r.entered}</td>)}
                      <td className="px-1.5 font-bold text-[#B01E28]">{data.trend.avg_entered_per_day}</td>
                    </tr>
                    <tr className="text-[13px] text-slate-700">
                      <td className="text-left text-[11px] text-slate-500 whitespace-nowrap py-1.5">AI $/part {DIR[data.trend.cost_direction]}</td>
                      {data.trend.rows.map((r) => <td key={r.date} className="px-1.5">{r.ai_parts ? `$${r.cost_per_part.toFixed(3)}` : "—"}</td>)}
                      <td className="px-1.5 font-bold text-[#B01E28]">${data.trend.avg_cost_per_part.toFixed(3)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
